import useSettings from "./useSettings";
import { SettingsHeader, Section, Textarea, Loading } from "./_shared";
import { AlertTriangle } from "lucide-react";

/**
 * Custom-scripts settings. Any HTML / JS pasted here is injected into every
 * public page — either inside <head> or just before </body>. Never runs in
 * the CMS/admin routes.
 *
 * Common use-cases:
 *  - Support / chat widget (Tawk.to, Crisp, Intercom, HubSpot)
 *  - Meta / Facebook Pixel
 *  - LinkedIn Insight tag
 *  - Any custom <link>, <meta>, <script> the admin needs
 */
export default function ScriptsSettingsAdmin() {
  const { settings, set, save, saving, saved } = useSettings();
  if (!settings) return <Loading />;

  return (
    <div data-testid="cms-settings-scripts">
      <SettingsHeader
        title="Scripts y widgets personalizados"
        description="Pegue aquí el código de widgets de soporte (Tawk.to, Crisp, Intercom…), píxeles de seguimiento o cualquier <script> / <meta> / <link> que quiera cargar en el sitio público."
        onSave={save}
        saving={saving}
        saved={saved}
      />

      <div className="bg-amber-50 border border-amber-200 p-4 mb-6 flex gap-3 text-sm text-amber-900">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
        <div>
          <p className="font-medium mb-1">Precaución</p>
          <p>
            El código se inyecta tal cual en la web pública. Un script mal formado o de origen no confiable
            puede romper la página o comprometer la seguridad. Pruebe primero en previa y guarde solo código
            que reconozca.
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <Section title="Inyectar en <head>">
          <p className="text-xs text-gray-500 mb-3">
            Se carga antes de que se muestre la página. Ideal para widgets de chat, píxeles y verificaciones de propiedad.
          </p>
          <Textarea
            label="Código HTML / JavaScript"
            rows={12}
            value={settings.custom_head_html}
            onChange={(v) => set("custom_head_html", v)}
            testId="custom-head-html"
          />
        </Section>

        <Section title="Inyectar antes de </body>">
          <p className="text-xs text-gray-500 mb-3">
            Se carga al final de la página. Ideal para scripts que no bloquean el renderizado.
          </p>
          <Textarea
            label="Código HTML / JavaScript"
            rows={12}
            value={settings.custom_body_html}
            onChange={(v) => set("custom_body_html", v)}
            testId="custom-body-html"
          />
        </Section>

        <Section title="Ejemplos rápidos">
          <details className="text-sm text-gray-700">
            <summary className="cursor-pointer font-medium text-[#0F2040] mb-2">Tawk.to (chat de soporte)</summary>
            <pre className="mt-3 p-3 bg-gray-50 border border-gray-200 text-xs overflow-x-auto">{`<!--Start of Tawk.to Script-->
<script type="text/javascript">
var Tawk_API=Tawk_API||{}, Tawk_LoadStart=new Date();
(function(){
  var s1=document.createElement("script"),s0=document.getElementsByTagName("script")[0];
  s1.async=true;
  s1.src='https://embed.tawk.to/XXXXX/default';
  s1.charset='UTF-8';
  s1.setAttribute('crossorigin','*');
  s0.parentNode.insertBefore(s1,s0);
})();
</script>
<!--End of Tawk.to Script-->`}</pre>
          </details>
          <details className="text-sm text-gray-700 mt-3">
            <summary className="cursor-pointer font-medium text-[#0F2040] mb-2">Crisp (chat de soporte)</summary>
            <pre className="mt-3 p-3 bg-gray-50 border border-gray-200 text-xs overflow-x-auto">{`<script type="text/javascript">
window.$crisp=[]; window.CRISP_WEBSITE_ID="XXXXXXXX-XXXX-XXXX";
(function(){var d=document;var s=d.createElement("script");
s.src="https://client.crisp.chat/l.js"; s.async=1; d.getElementsByTagName("head")[0].appendChild(s);})();
</script>`}</pre>
          </details>
          <details className="text-sm text-gray-700 mt-3">
            <summary className="cursor-pointer font-medium text-[#0F2040] mb-2">Meta Pixel</summary>
            <pre className="mt-3 p-3 bg-gray-50 border border-gray-200 text-xs overflow-x-auto">{`<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s){/*...*/}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
fbq('init', 'YOUR_PIXEL_ID');
fbq('track', 'PageView');
</script>`}</pre>
          </details>
        </Section>
      </div>
    </div>
  );
}
